import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { map, Observable, Subject } from 'rxjs';
import { FinancesState } from '@app/app/store/finances/finances.state';
import { Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { CurrencyPipe } from '@angular/common';
import { BorrowerValue } from '@app/app/interfaces/borrower-value.model';
import { isEmpty } from 'lodash';

declare interface DisplayValuesMap {
  [key: string]: {
    label: string;
    value: (val) => any;
  };
}

@Component({
  selector: 'app-credit-report',
  templateUrl: 'credit-report.component.html',
})
export class CreditReportComponent implements OnInit, OnDestroy {

  @Input() contactId!: number;
  @Input({required: true}) type!: 'commercial' | 'consumer';
  @Input() showEmptyState: boolean = false;

  creditReport$: Observable<{[key: string]: any}>;
  creditReportListValues$: Observable<Partial<BorrowerValue>[]>;
  destroyed$ = new Subject<boolean>();
  emptyStateVals: Partial<BorrowerValue>[];
  displayValuesMap: DisplayValuesMap;

  consumerDisplayableValuesMap: DisplayValuesMap = {
    publicRecordsCount: {
      label: '# of public records',
      value: (val) => val,
    },
    installmentBalance: {
      label: 'Installment balance',
      value: (val) => this.currencyPipe.transform(val),
    },
    revolvingBalance: {
      label: 'Revolving balance',
      value: (val) => this.currencyPipe.transform(val),
    },
    realEstateBalance: {
      label: 'Real estate balance',
      value: (val) => this.currencyPipe.transform(val),
    },
    revolvingAvailablePercent: {
      label: 'PCT revolving availability',
      value: (val) => `${val}%`,
    },
    inquiriesDuringLast6Months: {
      label: '6 month inquiries',
      value: (val) => val,
    },
    delinquenciesOver30Days: {
      label: '30 day delinquencies',
      value: (val) => val,
    },
    delinquenciesOver60Days: {
      label: '60 day delinquencies',
      value: (val) => val,
    },
    delinquenciesOver90Days: {
      label: '90 day delinquencies',
      value: (val) => val,
    },
    derogCounter: {
      label: '# of derogatory marks',
      value: (val) => val,
    }
  }

  commercialDisplayableValuesMap: DisplayValuesMap = {
    noPayRisk: {
      label: 'No pay risk',
      value: (val) => val?.toString(),
    },
    slowPayRisk: {
      label: 'Slow pay risk',
      value: (val) => val?.toString(),
    },
    currentVsIndustryDbt: {
      label: 'Current vs Industry DBT',
      value: (val) => val?.toString(),
    },
    quarterlyScoreTrends: {
      label: 'Quarterly score trends',
      value: (val) => val?.toString(),
    },
    tradelines: {
      label: 'Tradelines',
      value: (val) => val,
    },
    totalBalanceAcrossTradelines: {
      label: 'Total balance across tradelines',
      value: (val) => this.currencyPipe.transform(val),
    },
    inquiriesInLastNineMonths: {
      label: 'Inquiries in last 9 mo',
      value: val => val,
    },
    taxLiens: {
      label: 'Tax liens',
      value: val => val,
    },
    judgements: {
      label: 'Judgements',
      value: val => val,
    },
    collections: {
      label: 'Collections',
      value: val => val,
    },
    totalUccFilings: {
      label: 'Total UCC filings',
      value: val => val,
    },
    derogatoryRecords: {
      label: 'Derogatory records',
      value: val => val,
    }
  }

  constructor(
    private store: Store,
    private currencyPipe: CurrencyPipe
  ) {}

  ngOnInit():void {
    this.displayValuesMap = this.type === 'consumer'
      ? this.consumerDisplayableValuesMap
      : this.commercialDisplayableValuesMap;

    this.emptyStateVals = Object.keys(this.displayValuesMap).map( key => {
      return {
        shortName: this.displayValuesMap[key].label,
        value: 'N/A',
      }
    });

    const selector = this.type === 'consumer'
      ? FinancesState.creditReportByContactId(this.contactId)
      : FinancesState.commercialCredit;
    this.creditReport$ = this.store.select( selector );
    this.creditReportListValues$ = this.creditReport$.pipe(
      takeUntil(this.destroyed$),
      map( creditReport => {
        return !isEmpty(creditReport)
          ? this.formatValuesFromCreditReport(creditReport)
          : this.showEmptyState
            ? this.emptyStateVals
            : [];
      }),
    );

  }

  formatValuesFromCreditReport(creditReport) {
    return Object.keys(creditReport)
      .filter( crValueKey => !!this.displayValuesMap[crValueKey] )
      .map( crValueKey => {
        const mapConfig = this.displayValuesMap[crValueKey];
        return {
          shortName: mapConfig.label,
          value: mapConfig.value(creditReport[crValueKey]),
        };
      })
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
